import { useEffect, useRef } from "react";
import { useLenis } from "lenis/react";

// Scrolls to the element with id matching the URL hash when enabled.
// Waits until the element is available, and scrolls smoothly using Lenis or window.scrollTo.
// Only does this once per mount.
export function useScrollToUrlHash(enabled: boolean): void {
  const lenis = useLenis();
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    // If the hook is not enabled or has already scrolled, return
    if (!enabled || hasScrolledRef.current) return;

    const { hash } = window.location;
    // If there is no hash, return
    if (!hash) return;

    // Stop the browser from auto-scrolling when reloading
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    // Scroll to the target section using absolute pixel position
    // (and `force: true`) so Lenis always smooth-scrolls
    // and isn't blocked by a previous in-flight animation.
    const scrollToHash = (): boolean => {
      // Get the target element
      const target = document.querySelector(hash);
      // If the target element is not found, return
      if (!target) return false;

      // Compute absolute position from the element's rect (bounding client rect)
      // so we don't depend on Lenis's internal offset calculation
      // (which races with image loads and GSAP ScrollTrigger refreshes).
      const top = target.getBoundingClientRect().top + window.scrollY;

      if (lenis) {
        // `force: true` cancels any in-flight Lenis scroll (e.g. the 600ms
        // layout-shift retry can re-target mid-animation without snapping).
        lenis.scrollTo(top, { force: true });
      } else {
        window.scrollTo({ top, behavior: "smooth" });
      }
      return true;
    };

    // Section components are lazy-loaded inside <Suspense>; poll until the
    // target appears, then scroll once and re-scroll after layout settles.
    let attemptsLeft = 50; // ~5s at 100ms intervals
    // Poll until the target appears, then scroll once and re-scroll after layout settles.
    const intervalId = setInterval(() => {
      // If the target appears, scroll once and re-scroll after layout settles.
      if (scrollToHash()) {
        clearInterval(intervalId);
        // Mark that we've scrolled so we don't do it again.
        hasScrolledRef.current = true;
        // Late layout-shifts (images, fonts, ScrollTrigger) can drift the section's offset after our first scroll — correct it once.
        // Wait 600ms after the first scroll to allow for layout shifts and ensure the section is in the correct position.
        setTimeout(scrollToHash, 600);
      } else if (--attemptsLeft <= 0) {
        // If we've tried too many times, stop polling.
        clearInterval(intervalId);
      }
    }, 100); // Poll every 100ms

    // Clean up the interval when the component unmounts.
    return () => clearInterval(intervalId);
  }, [enabled, lenis]);
}
